import { Field } from '@atlaskit/form';
import { Stack, Text } from '@atlaskit/primitives';
import { RadioGroup } from '@atlaskit/radio';
import DatabasePicker from '@databases/content-picker/DatabasePicker';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  BacklinkDisplayType,
  type BrokenDatabase,
  type Database,
  type DatabaseField,
  type DatabaseFieldType
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, {
  type FunctionComponent,
  type SyntheticEvent,
  useCallback,
  useMemo
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type EntryBacklinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.BACKLINK>;
};

const EntryBacklinkFieldSettings: FunctionComponent<EntryBacklinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const displayAsLabelId = 'entry-backlink-display-as-label';
  const displaySourceLabel = formatMessage(messages.entryBacklinkDisplaySourceLabel);
  const displayAsLabel = formatMessage(messages.entryBacklinkDisplayAsLabel);

  const selectedDatabase = defaultStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdOptional(state, {
      contentId: field.settings.contentId ?? ''
    })
  );

  const displayTypeOptions = useMemo(
    () => [
      {
        label: formatMessage(messages.entryBacklinkDisplayAsEntryLinkOption),
        value: BacklinkDisplayType.LINK
      },
      {
        label: formatMessage(messages.entryBacklinkDisplayAsNumberOption),
        value: BacklinkDisplayType.NUMBER
      }
    ],
    [formatMessage]
  );

  const handleDatabaseChange = useCallback(
    (database: Database | BrokenDatabase | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_DATABASE_ID,
          fieldId: field.id,
          databaseSpace: database ? { key: database.space.key } : undefined,
          databaseId: database && 'id' in database ? database.id : undefined,
          contentId: database?.contentId
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleDisplayTypeChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_BACKLINK_DISPLAY_TYPE,
          fieldId: field.id,
          backlinkDisplayType: event.currentTarget.value as BacklinkDisplayType
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  return (
    <Stack space="space.050">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {displaySourceLabel}
          </Text>
        }
        name="entry-backlink-source-database"
        isRequired
      >
        {({ fieldProps }) => (
          <DatabasePicker
            inputId={fieldProps.id}
            isCompact
            onChange={handleDatabaseChange}
            selectedDatabase={selectedDatabase}
          />
        )}
      </Field>

      <Field
        label={
          <Text
            id={displayAsLabelId}
            size="small"
            weight="bold"
            color="color.text.subtle"
          >
            {displayAsLabel}
          </Text>
        }
        name="entry-backlink-display-as"
      >
        {() => (
          <RadioGroup
            aria-labelledby={displayAsLabelId}
            onChange={handleDisplayTypeChange}
            options={displayTypeOptions}
            value={field.settings.backlinkDisplayType}
          />
        )}
      </Field>
    </Stack>
  );
};

export default EntryBacklinkFieldSettings;
